<template>
	<v-card class="flex round-card background-2 w100p dms-component" elevation="1">
		<v-card-title class="pa-3">
			<p class="pl-2 pl-xl-3">File Storage</p>
			<v-spacer></v-spacer>
			<v-spacer></v-spacer>
			<v-row class="pa-0 ma-0 pr-4">
				<v-text-field class="hidden-xs-only font-weight-regular" v-show="expand"
					append-icon="search"
					label="Cerca"
					v-model="search"
					clearable
					hide-details outlined dense
				></v-text-field>
			</v-row>
			<v-tooltip top>
				<template v-slot:activator="{ on }">
					<v-btn @click="loadContent()" :loading="loading" v-on="on" icon>
						<v-icon>refresh</v-icon>
					</v-btn>
				</template>
				<span>Refresh</span>
			</v-tooltip>
			<v-tooltip top>
				<template v-slot:activator="{ on }">
					<v-btn v-on="on" @click="expand = !expand" icon>
						<v-icon>{{ expand ? 'expand_less' : 'expand_more' }}</v-icon>
					</v-btn>
				</template>
				<span>{{ expand ? 'Contrai' : 'Espandi' }}</span>
			</v-tooltip>
		</v-card-title>
		<v-expand-transition>
		<div v-show="expand">
		<v-divider></v-divider>
		<v-container fluid class="pa-4">
		<!-- Toolbar -->
		<v-row no-gutters class="mb-3 align-center">
			<!-- Search mobile -->
			<v-col cols="12" class="d-sm-none pb-2">
				<v-text-field
					v-model="search"
					append-icon="search"
					label="Cerca"
					clearable hide-details outlined dense
				></v-text-field>
			</v-col>
			<v-col cols="12" md="6">
				<v-breadcrumbs :items="breadcrumbItems" class="pa-0">
					<template v-slot:item="{ item }">
						<v-breadcrumbs-item
							:disabled="item.disabled"
							@click="navigateToFolder(item.folderId)"
							class="cursor-pointer"
						>
							{{ item.text }}
						</v-breadcrumbs-item>
					</template>
				</v-breadcrumbs>
			</v-col>
			<v-col cols="12" md="6" class="d-flex justify-end align-center">
				<v-btn-toggle v-model="viewMode" mandatory dense class="mr-2">
					<v-btn small value="grid"><v-icon small>mdi-view-grid</v-icon></v-btn>
					<v-btn small value="list"><v-icon small>mdi-view-list</v-icon></v-btn>
				</v-btn-toggle>
				<v-btn color="primary" small @click="newFolderDialog = true" class="mr-2">
					<v-icon left small class="d-none d-sm-inline">mdi-folder-plus</v-icon>
					Nuova Cartella
				</v-btn>
				<v-btn color="success" small class="upload-btn">
					<v-icon left small class="d-none d-sm-inline">mdi-upload</v-icon>
					Carica
					<input
						ref="fileInput"
						type="file"
						multiple
						class="upload-btn-input"
						@change="handleFileUpload"
					/>
				</v-btn>
				<v-btn color="success" small outlined class="upload-btn ml-2">
					<v-icon left small class="d-none d-sm-inline">mdi-folder-upload</v-icon>
					Carica cartella
					<input
						ref="folderInput"
						type="file"
						webkitdirectory
						multiple
						class="upload-btn-input"
						@change="handleFolderUpload"
					/>
				</v-btn>
			</v-col>
		</v-row>

		<!-- Search scope toggle -->
		<v-row v-if="search" no-gutters class="mb-2 align-center">
			<v-chip-group v-model="searchScope" mandatory>
				<v-chip small filter value="local">Cartella corrente</v-chip>
				<v-chip small filter value="global">Ovunque</v-chip>
			</v-chip-group>
		</v-row>

		<!-- Storage info -->
		<v-row v-if="!search || searchScope === 'local'" no-gutters class="mb-2 align-center">
			<v-spacer></v-spacer>
			<v-col cols="auto" class="text-caption grey--text">
				<v-icon x-small class="mr-1">mdi-harddisk</v-icon>{{ formatSize(storage.total) }}
			</v-col>
		</v-row>

		<!-- Bulk toolbar -->
		<v-card v-if="hasSelection" outlined class="mb-3 bulk-toolbar">
			<v-card-text class="d-flex align-center py-2">
				<v-icon class="mr-2" color="primary">mdi-checkbox-multiple-marked</v-icon>
				<span class="text-body-2">{{ selectionCount }} elemento/i selezionato/i</span>
				<v-spacer></v-spacer>
				<v-btn small text @click="openBulkMoveDialog"><v-icon left small>mdi-folder-move</v-icon>Sposta</v-btn>
				<v-btn small text @click="bulkDownload" :loading="bulkLoading"><v-icon left small>mdi-download</v-icon>Scarica ZIP</v-btn>
				<v-btn small text color="error" @click="bulkDeleteDialog = true"><v-icon left small>mdi-delete</v-icon>Elimina</v-btn>
				<v-btn small icon @click="clearSelection"><v-icon>mdi-close</v-icon></v-btn>
			</v-card-text>
		</v-card>

		<!-- Drop zone -->
		<div
			ref="dropZone"
			class="drop-zone"
			:class="{ 'drop-zone-active': isDragging, 'marquee-active': marquee.active }"
			style="max-height: 500px; overflow-y: auto; position: relative"
			@dragenter.prevent="onDragEnter"
			@dragover.prevent
			@dragleave.prevent="onDragLeave"
			@drop.prevent="handleDrop"
			@mousedown.left="onMarqueeMouseDown"
		>
			<!-- Marquee overlay -->
			<div v-if="marquee.active" class="marquee-rect" :style="{
				left: marquee.x + 'px',
				top: marquee.y + 'px',
				width: marquee.w + 'px',
				height: marquee.h + 'px'
			}"></div>
			<!-- Upload progress -->
			<v-progress-linear v-if="uploading" :value="uploadProgress" color="primary" class="mb-3" height="6" rounded></v-progress-linear>

			<!-- Cartelle -->
			<div v-if="filteredFolders.length && !isGlobalSearch" class="mb-4">
				<div class="text-overline grey--text mb-2">Cartelle</div>
				<!-- Vista griglia -->
				<v-row v-if="viewMode === 'grid'" dense>
					<v-col v-for="folder in filteredFolders" :key="'f-' + folder.id" cols="6" sm="4" md="3" lg="2">
						<v-card
							outlined
							class="text-center pa-3 cursor-pointer hover-card d-flex flex-column align-center justify-center"
							:class="{ 'dragging': dragItem && dragItem.type === 'folder' && dragItem.id === folder.id, 'drag-over': dragOverFolder === folder.id, 'selected-multi': selectedFolderIds.includes(folder.id) }"
							:data-folder-id="folder.id"
							style="height: 140px"
							draggable="true"
							@dragstart="onItemDragStart($event, 'folder', folder)"
							@dragend="onItemDragEnd"
							@dragover.prevent.stop="onFolderDragOver($event, folder)"
							@dragleave.prevent.stop="onFolderDragLeave"
							@drop.prevent.stop="onFolderDrop($event, folder)"
							@click="navigateToFolder(folder.id)"
							@contextmenu.prevent="showFolderMenu($event, folder)"
						>
							<v-icon size="48" color="amber darken-2">mdi-folder</v-icon>
							<div class="text-body-2 mt-1 text-truncate">{{ folder.name }}</div>
							<div class="text-caption grey--text">{{ folder.documents_count || 0 }} file<span v-if="folder.documents_sum_size"> · {{ formatSize(folder.documents_sum_size) }}</span></div>
						</v-card>
					</v-col>
				</v-row>
				<!-- Vista lista -->
				<v-simple-table v-else dense class="bordered-table">
					<tbody>
						<tr
							v-for="folder in filteredFolders"
							:key="'f-' + folder.id"
							class="cursor-pointer"
							:class="{ 'dragging': dragItem && dragItem.type === 'folder' && dragItem.id === folder.id, 'drag-over': dragOverFolder === folder.id }"
							draggable="true"
							@dragstart="onItemDragStart($event, 'folder', folder)"
							@dragend="onItemDragEnd"
							@dragover.prevent.stop="onFolderDragOver($event, folder)"
							@dragleave.prevent.stop="onFolderDragLeave"
							@drop.prevent.stop="onFolderDrop($event, folder)"
							@click="navigateToFolder(folder.id)"
							@contextmenu.prevent="showFolderMenu($event, folder)"
						>
							<td style="width: 40px"><v-icon color="amber darken-2">mdi-folder</v-icon></td>
							<td>{{ folder.name }}</td>
						</tr>
					</tbody>
				</v-simple-table>
			</div>

			<!-- Documenti -->
			<div v-if="filteredDocuments.length">
				<div class="text-overline grey--text mb-2">Documenti</div>
				<!-- Vista griglia -->
				<v-row v-if="viewMode === 'grid'" dense>
					<v-col v-for="doc in filteredDocuments" :key="'d-' + doc.id" cols="6" sm="4" md="3" lg="2">
						<v-card
							outlined
							class="dms-doc-card"
							:class="{ 'selected': selectedDoc && selectedDoc.id === doc.id, 'dragging': dragItem && dragItem.type === 'doc' && dragItem.id === doc.id, 'selected-multi': selectedDocIds.includes(doc.id) }"
							:data-doc-id="doc.id"
							draggable="true"
							@dragstart="onItemDragStart($event, 'doc', doc)"
							@dragend="onItemDragEnd"
							@click="toggleSelectDocument(doc)"
							@dblclick.stop="isPreviewable(doc) ? openPreview(doc) : downloadDoc(doc)"
							@contextmenu.prevent="showDocMenu($event, doc)"
						>
							<div class="dms-doc-preview">
								<img v-if="doc.thumbnail" :src="'data:image/png;base64,' + doc.thumbnail" />
								<v-icon v-else size="64" :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
								<v-chip v-if="isCoverDoc(doc)" x-small color="amber darken-2" text-color="white" class="dms-doc-mobile-flag">Copertina</v-chip>
								<v-chip v-else-if="isMobileHidden(doc)" x-small color="blue-grey" text-color="white" class="dms-doc-mobile-flag">non visibile sul mobile</v-chip>
								<div class="dms-doc-tags" v-if="doc.tags && doc.tags.length">
									<v-chip v-for="tag in doc.tags.slice(0, 2)" :key="tag.id" x-small :color="tag.color || 'grey'" text-color="white">{{ tag.name }}</v-chip>
									<v-chip v-if="doc.tags.length > 2" x-small color="grey" text-color="white">+{{ doc.tags.length - 2 }}</v-chip>
								</div>
							</div>
							<div class="dms-doc-info">
								<v-icon size="18" :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
								<span class="text-body-2 text-truncate" style="flex: 1; min-width: 0">{{ doc.name | fileName }}.{{ doc.ext }}</span>
								<v-btn icon x-small @click.stop="showDocMenu($event, doc)"><v-icon>mdi-dots-vertical</v-icon></v-btn>
							</div>
						</v-card>
					</v-col>
				</v-row>
				<!-- Vista lista -->
				<v-simple-table v-else dense class="bordered-table">
					<thead>
						<tr>
							<th style="width: 40px"></th>
							<th>Nome</th>
							<th v-if="isGlobalSearch" class="d-none d-sm-table-cell">Cartella</th>
							<th class="d-none d-sm-table-cell">Dimensione</th>
							<th class="d-none d-sm-table-cell">Data</th>
							<th class="d-none d-md-table-cell">Caricato da</th>
							<th class="d-none d-md-table-cell">Tag</th>
							<th style="width: 60px"></th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="doc in filteredDocuments"
							:key="'d-' + doc.id"
							class="cursor-pointer"
							:class="{ 'dragging': dragItem && dragItem.type === 'doc' && dragItem.id === doc.id }"
							draggable="true"
							@dragstart="onItemDragStart($event, 'doc', doc)"
							@dragend="onItemDragEnd"
							@click="debouncedOpenDocDetails(doc)"
							@dblclick.stop="cancelDebouncedClick(); isPreviewable(doc) ? openPreview(doc) : downloadDoc(doc)"
							@contextmenu.prevent="showDocMenu($event, doc)"
						>
							<td><v-icon :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon></td>
							<td>
								{{ doc.name | fileName(doc.ext) }}
								<v-chip v-if="isCoverDoc(doc)" x-small color="amber darken-2" text-color="white" class="ml-1">Copertina</v-chip>
								<v-chip v-else-if="isMobileHidden(doc)" x-small color="blue-grey" text-color="white" class="ml-1">non visibile sul mobile</v-chip>
							</td>
							<td v-if="isGlobalSearch" class="d-none d-sm-table-cell text-no-wrap text-caption">
								<v-icon x-small class="mr-1">mdi-folder</v-icon>{{ doc.folder ? doc.folder.name : 'Home' }}
							</td>
							<td class="d-none d-sm-table-cell text-no-wrap">{{ formatSize(doc.size) }}</td>
							<td class="d-none d-sm-table-cell text-no-wrap">{{ doc.created_at | date }}</td>
							<td class="d-none d-md-table-cell text-no-wrap">{{ doc.uploaded_by ? doc.uploaded_by.name : '-' }}</td>
							<td class="d-none d-md-table-cell">
								<v-chip v-for="tag in (doc.tags || []).slice(0, 3)" :key="tag.id" x-small :color="tag.color || 'grey'" text-color="white" class="mr-1">
									{{ tag.name }}
								</v-chip>
								<span v-if="(doc.tags || []).length > 3" class="text-caption grey--text">+{{ doc.tags.length - 3 }}</span>
							</td>
							<td>
								<v-btn icon x-small @click.stop="downloadDoc(doc)"><v-icon small>mdi-download</v-icon></v-btn>
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</div>

			<!-- Empty state -->
			<div v-if="!loading && !filteredFolders.length && !filteredDocuments.length" class="empty-state text-center">
				<v-icon size="64" color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
				<div class="text-h6 grey--text mt-3">Nessun contenuto</div>
				<div class="text-body-2 grey--text">Trascina file qui o usa il pulsante Carica</div>
			</div>

			<v-progress-circular v-if="loading" indeterminate color="primary" class="d-block mx-auto my-12"></v-progress-circular>

			<!-- Drag overlay -->
			<div v-if="isDragging" class="drop-overlay">
				<v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
				<div class="text-h6 primary--text mt-2">Rilascia i file qui</div>
			</div>
		</div>

		<!-- Context menu -->
		<v-menu v-model="contextMenu.show" :position-x="contextMenu.x" :position-y="contextMenu.y" absolute content-class="round-card">
			<v-list dense>
				<v-list-item @click="openDocDetails(contextMenu.item)" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-information-outline</v-icon></v-list-item-icon>
					<v-list-item-title>Dettagli</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadDoc(contextMenu.item)" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-download</v-icon></v-list-item-icon>
					<v-list-item-title>Scarica</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadFolder(contextMenu.item)" v-if="contextMenu.type === 'folder'">
					<v-list-item-icon><v-icon small>mdi-folder-download</v-icon></v-list-item-icon>
					<v-list-item-title>Scarica cartella</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openRenameDialog()">
					<v-list-item-icon><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
					<v-list-item-title>Rinomina</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openMoveDialog()">
					<v-list-item-icon><v-icon small>mdi-folder-move</v-icon></v-list-item-icon>
					<v-list-item-title>Sposta</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openTagDialog()" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-tag</v-icon></v-list-item-icon>
					<v-list-item-title>Tag</v-list-item-title>
				</v-list-item>
				<v-list-item @click="assignToVariant()" v-if="canAssignToVariant">
					<v-list-item-icon><v-icon small>mdi-image-multiple-outline</v-icon></v-list-item-icon>
					<v-list-item-title>Assegna a varianti</v-list-item-title>
				</v-list-item>
				<v-list-item @click="confirmDelete()">
					<v-list-item-icon><v-icon small color="red">mdi-delete</v-icon></v-list-item-icon>
					<v-list-item-title class="red--text">Elimina</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>

		<!-- Dettagli documento (sidebar) -->
		<v-navigation-drawer v-model="detailDrawer" right app clipped temporary width="380">
			<template v-if="selectedDoc">
				<v-card flat>
					<v-card-title class="d-flex align-center" style="flex-wrap: nowrap">
						<v-icon left :color="getFileColor(selectedDoc.ext)" class="flex-shrink-0">{{ getFileIcon(selectedDoc.ext) }}</v-icon>
						<span class="text-subtitle-1 text-truncate" style="min-width: 0; flex: 1">{{ selectedDoc.name | fileName(selectedDoc.ext) }}</span>
						<v-btn icon small class="flex-shrink-0 ml-auto" @click="detailDrawer = false"><v-icon>mdi-close</v-icon></v-btn>
					</v-card-title>
					<v-divider></v-divider>
					<v-card-text>
						<div class="mb-3">
							<div class="text-caption grey--text">Nome file</div>
							<div>{{ selectedDoc.name | fileName(selectedDoc.ext) }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Dimensione</div>
							<div>{{ formatSize(selectedDoc.size) }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Data caricamento</div>
							<div>{{ selectedDoc.created_at | date }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Caricato da</div>
							<div>{{ selectedDoc.uploaded_by ? selectedDoc.uploaded_by.name : '-' }}</div>
						</div>
						<div class="mb-3" v-if="selectedDoc.description">
							<div class="text-caption grey--text">Descrizione</div>
							<div>{{ selectedDoc.description }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Versione</div>
							<div>{{ selectedDoc.version }}</div>
						</div>
						<div class="mb-3" v-if="selectedDoc.tags && selectedDoc.tags.length">
							<div class="text-caption grey--text">Tag</div>
							<v-chip v-for="tag in selectedDoc.tags" :key="tag.id" small :color="tag.color || 'grey'" text-color="white" class="mr-1 mt-1">
								{{ tag.name }}
							</v-chip>
						</div>

						<!-- Versioni -->
						<div class="mb-3" v-if="docVersions.length">
							<div class="text-caption grey--text mb-1">Versioni precedenti</div>
							<v-list dense class="pa-0">
								<v-list-item v-for="ver in docVersions" :key="ver.id" dense>
									<v-list-item-content>
										<v-list-item-title class="text-body-2">v{{ ver.version }} - {{ ver.original_name }}</v-list-item-title>
										<v-list-item-subtitle>{{ ver.created_at | date }} - {{ formatSize(ver.size) }}</v-list-item-subtitle>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</div>
					</v-card-text>
					<v-card-actions>
						<v-btn color="primary" outlined @click="downloadDoc(selectedDoc)">
							<v-icon left small>mdi-download</v-icon>Scarica
						</v-btn>
						<v-btn color="success" text @click="$refs.versionInput.click()">
							<v-icon left small>mdi-upload</v-icon>Nuova Versione
						</v-btn>
						<input
							ref="versionInput"
							type="file"
							style="position: absolute; width: 0; height: 0; overflow: hidden; opacity: 0"
							@change="handleVersionUpload"
						/>
					</v-card-actions>
				</v-card>
			</template>
		</v-navigation-drawer>

		<DmsRecentActivity ref="recentActivity" :api-base="apiBase" />

		<!-- Dialog nuova cartella -->
		<v-dialog v-model="newFolderDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Nuova Cartella</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-text-field v-model="newFolderName" label="Nome cartella" outlined dense autofocus @keyup.enter="createFolder"></v-text-field>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="newFolderDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="createFolder" :loading="saving">Crea</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog rinomina -->
		<v-dialog v-model="renameDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Rinomina</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-text-field v-model="renameName" label="Nuovo nome" outlined dense autofocus @keyup.enter="renameItem" :rules="[v => !/[\\\\/:*?&quot;<>|]/.test(v) || 'Caratteri non consentiti: \\\\ / : * ? &quot; < > |']"></v-text-field>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="renameDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="renameItem" :loading="saving">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog tag -->
		<v-dialog v-model="tagDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Gestisci Tag</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-combobox
						ref="tagCombobox"
						v-model="selectedTags"
						:items="allTags"
						item-text="name"
						item-value="id"
						label="Tag"
						multiple
						chips
						small-chips
						deletable-chips
						outlined
						dense
					></v-combobox>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="tagDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="saveTags" :loading="saving">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog sposta -->
		<v-dialog v-model="moveDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Sposta in...</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-list dense class="pa-0">
						<v-list-item @click="moveFolderTarget = null" :class="{ 'v-list-item--active primary--text': moveFolderTarget === null }">
							<v-list-item-icon class="mr-2 pr-2"><v-icon color="amber darken-2">mdi-folder-home</v-icon></v-list-item-icon>
							<v-list-item-title>Home</v-list-item-title>
						</v-list-item>
					</v-list>
					<v-treeview
						:items="folderTree"
						item-key="id"
						item-text="name"
						activatable
						@update:active="moveFolderTarget = $event[0] || null"
						open-on-click
						dense
					>
						<template v-slot:prepend="{}">
							<v-icon color="amber darken-2">mdi-folder</v-icon>
						</template>
					</v-treeview>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="moveDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="moveItem" :loading="saving">Sposta</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog conferma eliminazione -->
		<v-dialog v-model="deleteDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Conferma Eliminazione</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					Sei sicuro di voler eliminare <strong>{{ contextMenu.item ? contextMenu.item.name : '' }}</strong>?
					<span v-if="contextMenu.type === 'folder'">Tutti i contenuti della cartella verranno eliminati.</span>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="deleteDialog = false">Annulla</v-btn>
					<v-btn text color="error" @click="deleteItem" :loading="saving">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: bulk move -->
		<v-dialog v-model="bulkMoveDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Sposta {{ selectionCount }} elemento/i</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<p class="mb-2">Scegli la cartella di destinazione.</p>
					<v-select
						v-model="bulkMoveFolderTarget"
						:items="[{ id: null, name: '— Radice —' }].concat(folders)"
						item-text="name"
						item-value="id"
						label="Cartella destinazione"
						outlined dense clearable
					></v-select>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkMoveDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="confirmBulkMove" :loading="bulkLoading">Sposta</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: bulk delete -->
		<v-dialog v-model="bulkDeleteDialog" max-width="450" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Conferma eliminazione</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					Eliminare <strong>{{ selectedDocIds.length }}</strong> documento/i e <strong>{{ selectedFolderIds.length }}</strong> cartella/e?
					<span v-if="selectedFolderIds.length">
						<br><br><v-icon small color="warning">mdi-alert</v-icon>
						Le cartelle saranno eliminate con tutto il loro contenuto.
					</span>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkDeleteDialog = false">Annulla</v-btn>
					<v-btn text color="error" @click="confirmBulkDelete" :loading="bulkLoading">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<file-preview-dialog
			v-model="previewDialog"
			:doc="previewDoc"
			:url="previewUrl"
		/>

		<v-snackbar v-model="snackbar.value" :color="snackbar.color" :timeout="snackbar.timeout || 4000">
			{{ snackbar.text }}
			<template v-slot:action="{ attrs }">
				<v-btn dark icon v-bind="attrs" @click="snackbar.value = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
	</v-container>
	</div>
	</v-expand-transition>
	</v-card>
</template>

<script>
import DmsMixin from '@/mixins/DmsMixin'
import FilePreviewDialog from '@/components/common/FilePreviewDialog'
import DmsRecentActivity from '@/components/common/DmsRecentActivity'
import { isImageExt } from '@/components/utilities/imageFiles'

export default {
	name: 'DmsUploader',
	mixins: [DmsMixin],
	components: { DmsRecentActivity, FilePreviewDialog },
	props: {
		id: { type: Number, required: true },
		attachableType: { type: String, required: true }
	},

	data() {
		return {
			expand: true,
			// Lo annuncia chi sa gestire l'assegnazione (ProductPhotoAssignment) dopo aver
			// letto le varianti: il DMS e' generico e non conosce il dominio prodotti.
			// Default false → nessuna voce fantasma dove non c'e' niente da assegnare.
			variantAssignAvailable: false,
			// SOLO attachable_type='product': {attachment_id: {variant_ids, is_cover}}
			// da attachmentslists — l'unica fonte coi legami foto↔varianti idratati.
			// null = mai caricata (nessuna etichetta finche' non si sa la verita').
			productPhotoMap: null
		}
	},

	computed: {
		// "Assegna a varianti" ha senso solo sulle IMMAGINI di un PRODOTTO con
		// varianti salvate: il legame in pivot punta a varianti, non a un documento
		// qualsiasi, e su un prodotto semplice non c'e' nessuno a cui assegnare.
		canAssignToVariant() {
			if (!this.variantAssignAvailable) return false
			if (this.attachableType !== 'product') return false
			if (!this.contextMenu || this.contextMenu.type !== 'doc' || !this.contextMenu.item) return false
			return isImageExt(this.contextMenu.item.ext)
		},

		// App mobile attiva per il tenant (websites.mobile_enabled, servito da
		// user-details). Stesso wrapper dei settings per-entita' che gateano il
		// toggle desktop/mobile.
		isMobileEnabled() {
			return !!this.$store.getters.isMobileEnabled
		}
	},

	watch: {
		// La show riusa l'istanza al cambio di :id: la mappa legami del prodotto
		// precedente etichetterebbe le foto del nuovo. Reset + reload.
		id() {
			this.productPhotoMap = null
			this.refreshProductPhotoMap()
		}
	},

	mounted() {
		if (this.attachableType && this.$store.getters.checkSettings('compact_' + this.attachableType + '_view')) {
			this.expand = false
		} else {
			this.expand = !this.$store.getters.checkSettings('compact_viewing')
		}
		this.$eventBusUtility.$on('refreshFileUploader', () => {
			this.loadContent()
			this.refreshProductPhotoMap()
		})
		this.$eventBusUtility.$on('variantPhotoAssignAvailable', this.setVariantAssignAvailable)
		this.$eventBusUtility.$on('variantPhotoAssignmentsChanged', this.refreshProductPhotoMap)
		this.refreshProductPhotoMap()
	},

	beforeDestroy() {
		this.$eventBusUtility.$off('refreshFileUploader')
		this.$eventBusUtility.$off('variantPhotoAssignAvailable', this.setVariantAssignAvailable)
		this.$eventBusUtility.$off('variantPhotoAssignmentsChanged', this.refreshProductPhotoMap)
	},
	
	methods: {
		dmsConfig() {
			return {
				apiBase: 'dms/' + this.attachableType + '/' + this.id,
				breadcrumbRootLabel: 'Home',
				viewModeStorageKey: 'dms-uploader-view-mode',
				hasRecentActivity: true
			}
		},

		setVariantAssignAvailable(available) {
			this.variantAssignAvailable = !!available
		},

		/**
		 * Input:  nessuno (usa attachableType + id)
		 * Fa:     SOLO per attachable_type='product' carica da attachmentslists la
		 *         mappa {attachment_id: {variant_ids, is_cover}} — la regola
		 *         "non visibile sul mobile" esiste solo per le foto prodotto
		 * Output: nessuno (aggiorna productPhotoMap; su altri type resta null)
		 */
		refreshProductPhotoMap() {
			if (this.attachableType !== 'product' || !this.id) return
			this.axios.post('attachmentslists/', { attachable_type: 'product', id: this.id }).then((response) => {
				const map = {}
				const attachments = Array.isArray(response.data.attachments) ? response.data.attachments : []
				attachments.forEach((att) => {
					map[att.id] = {
						variant_ids: att.variant_ids || [],
						is_cover: !!att.is_cover
					}
				})
				this.productPhotoMap = map
			}, () => {})
		},

		isCoverDoc(doc) {
			if (this.attachableType !== 'product' || !this.productPhotoMap) return false
			const info = this.productPhotoMap[doc.id]
			return !!(info && info.is_cover)
		},

		/**
		 * Input:  documento del DMS
		 * Fa:     vera quando la foto prodotto NON arrivera' al telefono — stessa
		 *         regola che il backend applica nel ramo mobile: non copertina e
		 *         nessuna variante che la reclama. Scoped: su ogni altro
		 *         attachableType (ticket, progetti, ...) sempre falsa.
		 * Output: boolean
		 */
		isMobileHidden(doc) {
			// L'etichetta parla dell'app mobile: su un tenant che non ce l'ha
			// non ha referente, e manderebbe l'admin a caccia di un problema
			// inesistente. La copertina invece resta etichettata: quella e' un
			// dato del prodotto, valido anche senza app.
			if (!this.isMobileEnabled) return false
			if (this.attachableType !== 'product' || !this.productPhotoMap) return false
			if (!isImageExt(doc.ext)) return false
			const info = this.productPhotoMap[doc.id] || { variant_ids: [], is_cover: false }
			return !info.is_cover && !info.variant_ids.length
		},

		// Il DMS non conosce le varianti del prodotto: passa il documento a chi sa
		// cosa farci (ProductPhotoAssignment, montato sotto nella show prodotto).
		assignToVariant() {
			const doc = this.contextMenu.item
			this.contextMenu.show = false
			this.$eventBusUtility.$emit('assignVariantPhoto', doc)
		}
	}
}
</script>

<style src="@/css/dms.css"></style>

<style scoped>
.marquee-rect {
	position: absolute;
	border: 1px dashed var(--v-primary-base, #1976d2);
	background: rgba(25, 118, 210, 0.08);
	pointer-events: none;
	z-index: 5;
}
.dms-doc-card.selected-multi,
.v-card.selected-multi {
	outline: 2px solid var(--v-primary-base, #1976d2);
	outline-offset: -2px;
}
.bulk-toolbar {
	position: sticky;
	top: 0;
	z-index: 4;
}
.dms-doc-preview {
	position: relative;
}
.dms-doc-mobile-flag {
	position: absolute;
	top: 6px;
	left: 6px;
}
</style>
